/**
 * Session Manager Component
 * Provides UI for managing browser sessions - start, stop, list active sessions
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Skeleton } from '../ui/skeleton'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog'
import { Label } from '../ui/label'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Switch } from '../ui/switch'
import { useToast } from '../../hooks/use-toast'
import { api, type BrowserSession, type CreateSessionRequest } from '../../lib/api'
import { Play, Square, ExternalLink, RefreshCw, Monitor } from 'lucide-react'

interface SessionManagerProps {
  onSessionSelect?: (session: BrowserSession) => void
  selectedSessionId?: string
  showSessionPicker?: boolean
}

export function SessionManager({ 
  onSessionSelect, 
  selectedSessionId, 
  showSessionPicker = false 
}: SessionManagerProps) {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [createSessionData, setCreateSessionData] = useState<CreateSessionRequest>({
    proxy_country_code: 'us',
    use_proxy: false,
    use_adblock: true,
    browser_viewport_width: 1280,
    browser_viewport_height: 960
  })
  
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Query for listing sessions
  const {
    data: sessionsData,
    isLoading: isLoadingSessions,
    error: sessionsError,
    refetch: refetchSessions
  } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.sessions.listSessions(),
    refetchInterval: false, // Disable automatic refresh to prevent spam
    staleTime: 60000 // Consider data stale after 60 seconds
  })

  // Mutation for creating sessions
  const createSessionMutation = useMutation({
    mutationFn: (data: CreateSessionRequest) => api.sessions.createSession(data),
    onSuccess: (newSession) => {
      toast({
        title: 'Session Created',
        description: `Browser session ${newSession.id} has been created successfully.`
      })
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
      setIsCreateDialogOpen(false)
      if (onSessionSelect) {
        onSessionSelect(newSession)
      }
    },
    onError: (error) => {
      toast({
        title: 'Failed to Create Session',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive'
      })
    }
  })

  // Mutation for stopping sessions
  const stopSessionMutation = useMutation({
    mutationFn: (sessionId: string) => api.sessions.stopSession(sessionId),
    onSuccess: (_, sessionId) => {
      toast({
        title: 'Session Stopped',
        description: `Browser session ${sessionId} has been stopped.`
      })
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
    },
    onError: (error) => {
      toast({
        title: 'Failed to Stop Session',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive'
      })
    }
  })

  const handleCreateSession = () => {
    createSessionMutation.mutate(createSessionData)
  }

  const handleStopSession = (sessionId: string) => {
    stopSessionMutation.mutate(sessionId)
  }

  const handleSessionSelect = (session: BrowserSession) => {
    if (onSessionSelect) {
      onSessionSelect(session)
    }
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'default'
      case 'stopped':
        return 'secondary'
      case 'inactive':
        return 'outline'
      default:
        return 'outline'
    }
  }

  const activeSessions = sessionsData?.sessions?.filter(s => s.status === 'active') || []
  const inactiveSessions = sessionsData?.sessions?.filter(s => s.status !== 'active') || []

  if (sessionsError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Monitor className="h-5 w-5" />
            Browser Sessions
          </CardTitle>
          <CardDescription>
            Failed to load sessions: {sessionsError instanceof Error ? sessionsError.message : 'Unknown error'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => refetchSessions()} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Monitor className="h-5 w-5" />
              Browser Sessions
            </CardTitle>
            <CardDescription>
              Manage active browser sessions for automation tasks
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => refetchSessions()} 
              variant="outline" 
              size="sm"
              disabled={isLoadingSessions}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingSessions ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Play className="h-4 w-4 mr-2" />
                  Start Session
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Browser Session</DialogTitle>
                  <DialogDescription>
                    Configure a new browser session for automation tasks
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="viewport-width">Viewport Width</Label>
                      <Input
                        id="viewport-width"
                        type="number"
                        value={createSessionData.browser_viewport_width}
                        onChange={(e) => setCreateSessionData(prev => ({
                          ...prev,
                          browser_viewport_width: parseInt(e.target.value) || 1280
                        }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="viewport-height">Viewport Height</Label>
                      <Input
                        id="viewport-height"
                        type="number"
                        value={createSessionData.browser_viewport_height}
                        onChange={(e) => setCreateSessionData(prev => ({
                          ...prev,
                          browser_viewport_height: parseInt(e.target.value) || 960
                        }))}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="proxy-country">Proxy Country</Label>
                    <Select 
                      value={createSessionData.proxy_country_code} 
                      onValueChange={(value) => setCreateSessionData(prev => ({
                        ...prev,
                        proxy_country_code: value
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="us">United States</SelectItem>
                        <SelectItem value="uk">United Kingdom</SelectItem>
                        <SelectItem value="ca">Canada</SelectItem>
                        <SelectItem value="de">Germany</SelectItem>
                        <SelectItem value="fr">France</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="use-proxy">Use Proxy</Label>
                    <Switch
                      id="use-proxy"
                      checked={createSessionData.use_proxy}
                      onCheckedChange={(checked) => setCreateSessionData(prev => ({
                        ...prev,
                        use_proxy: checked
                      }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="use-adblock">Use Ad Blocker</Label>
                    <Switch
                      id="use-adblock"
                      checked={createSessionData.use_adblock}
                      onCheckedChange={(checked) => setCreateSessionData(prev => ({
                        ...prev,
                        use_adblock: checked
                      }))}
                    />
                  </div>

                  <Button 
                    onClick={handleCreateSession} 
                    className="w-full"
                    disabled={createSessionMutation.isPending}
                  >
                    {createSessionMutation.isPending ? 'Creating...' : 'Create Session'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoadingSessions ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-8 w-20" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {activeSessions.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Active Sessions ({activeSessions.length})</h4>
                <div className="space-y-2">
                  {activeSessions.map((session) => (
                    <div 
                      key={session.id} 
                      className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${
                        showSessionPicker && selectedSessionId === session.id 
                          ? 'border-primary bg-primary/5' 
                          : 'hover:bg-muted/50'
                      } ${showSessionPicker ? 'cursor-pointer' : ''}`}
                      onClick={() => showSessionPicker && handleSessionSelect(session)}
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm">{session.id}</span>
                          <Badge variant={getStatusBadgeVariant(session.status)}>
                            {session.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Created {new Date(session.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {session.live_view_url && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation()
                              window.open(session.live_view_url, '_blank')
                            }}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleStopSession(session.id)
                          }}
                          disabled={stopSessionMutation.isPending}
                        >
                          <Square className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {inactiveSessions.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Recent Sessions ({inactiveSessions.length})</h4>
                <div className="space-y-2">
                  {inactiveSessions.slice(0, 3).map((session) => (
                    <div key={session.id} className="flex items-center justify-between p-3 border rounded-lg opacity-60">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm">{session.id}</span>
                          <Badge variant={getStatusBadgeVariant(session.status)}>
                            {session.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {session.stopped_at 
                            ? `Stopped ${new Date(session.stopped_at).toLocaleString()}`
                            : `Created ${new Date(session.created_at).toLocaleString()}`
                          }
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeSessions.length === 0 && inactiveSessions.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Monitor className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No browser sessions found</p>
                <p className="text-sm">Create a new session to get started</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
